import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const AboutManager = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await adminApi.getAbout();
      setAbout(response.data);
    } catch (error) {
      console.error('Error fetching about:', error);
      toast.error('Failed to load about content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setAbout({ ...about, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateAbout(about);
      toast.success('About section updated successfully');
    } catch (error) {
      console.error('Error saving about:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="about-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">About Section</h1>
          <p className="text-muted-foreground">Manage your about us content.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-about-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="admin-card space-y-6">
        <div className="form-group">
          <label htmlFor="title" className="form-label">Section Title</label>
          <Input
            id="title"
            name="title"
            value={about?.title || ''}
            onChange={handleChange}
            placeholder="About Us"
            className="input-neon"
            data-testid="about-title-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description" className="form-label">Description</label>
          <Textarea
            id="description"
            name="description"
            value={about?.description || ''}
            onChange={handleChange}
            placeholder="We are a team of passionate developers..."
            rows={4}
            className="input-neon resize-none"
            data-testid="about-description-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="mission" className="form-label">Mission Statement</label>
          <Textarea
            id="mission"
            name="mission"
            value={about?.mission || ''}
            onChange={handleChange}
            placeholder="Our mission is to..."
            rows={3}
            className="input-neon resize-none"
            data-testid="about-mission-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="image_url" className="form-label">Team Image URL</label>
          <Input
            id="image_url"
            name="image_url"
            value={about?.image_url || ''}
            onChange={handleChange}
            placeholder="https://..."
            className="input-neon"
            data-testid="about-image-input"
          />
        </div>

        {about?.image_url && (
          <div className="rounded-xl overflow-hidden max-w-md">
            <img src={about.image_url} alt="Preview" className="w-full h-auto" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutManager;
